import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { IconSend } from '@components/icons/send';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { api } from 'api';

import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

import { HireflixPositionSuccess } from './types';
import { getErrorMessage, getNotificationText } from './utils';

type Props = ModalProps<{
  appID: string;
  positionData: HireflixPositionSuccess;
}>;

export const HireflixSendInterviewModal = observer((props: Props) => {
  const { closeModal, appID, positionData } = props;
  const { applicationStore, t } = useStore();
  const { showError } = useToaster();
  const client = useQueryClient();

  const application = applicationStore.items.get(appID);

  const [submitting, setSubmitting] = useState(false);
  async function submit() {
    setSubmitting(true);
    const res = await api.integrations.hireflixSendInterview(appID);
    setSubmitting(false);
    if (!res.error) {
      const data = res.data;
      if (data && data.error) {
        showError(getErrorMessage(data.error, t));
      } else {
        closeModal();
        client.invalidateQueries(['hireflixInterview', appID]);
        application.load();
      }
    } else {
      showError(t(`common.errors.${res.status}`));
    }
  }
  return (
    <Modal>
      <Modal.Header text={t('integrations.hireflix.application.modal.header')} />
      <Stack space={1}>
        <Text
          font="semibold14"
          text={t('integrations.hireflix.application.modal.position.label')}
        />
        <Text font="regular16" text={positionData.name} />
      </Stack>
      {positionData.notifications && (
        <Stack space={1}>
          <Text
            font="semibold14"
            text={t('integrations.hireflix.application.modal.notifications.label')}
          />
          {positionData.notifications.map((notification, idx) => (
            <Text key={idx} font="regular16" text={getNotificationText(notification, t)} />
          ))}
        </Stack>
      )}
      <Modal.Actions>
        <Button
          variant="secondary"
          onClick={!submitting ? closeModal : undefined}
          isDisabled={submitting}
          text={t('modals.confirm.button.cancel')}
        />
        <Button
          variant="primary"
          onClick={!submitting ? submit : undefined}
          isDisabled={submitting}
          text={t('integrations.hireflix.application.modal.submit')}
          iconLeft={<IconSend />}
        />
      </Modal.Actions>
    </Modal>
  );
});
